import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'

interface ChatScrollState {
  container: RefObject<HTMLDivElement | null>
  atBottom: boolean
  newMessages: boolean
  jump: () => void
  onScroll: () => void
}

export function useChatScroll(lastId: string | undefined): ChatScrollState {
  const container = useRef<HTMLDivElement>(null)
  const previous = useRef<string | undefined>(undefined)
  const nearBottom = useRef(true)
  const [atBottom, setAtBottom] = useState(true)
  const [newMessages, setNewMessages] = useState(false)
  const jump = useCallback(() => {
    const element = container.current
    if (element) element.scrollTop = element.scrollHeight
    nearBottom.current = true; setAtBottom(true); setNewMessages(false)
  }, [])
  useEffect(() => {
    if (!lastId || lastId === previous.current) return
    if (!previous.current || nearBottom.current) jump()
    else setNewMessages(true)
    previous.current = lastId
  }, [lastId, jump])
  useEffect(() => {
    const element = container.current
    if (!element) return
    const keepBottom = () => { if (nearBottom.current) jump() }
    const resize = new ResizeObserver(keepBottom)
    const observeContent = () => {
      resize.disconnect()
      resize.observe(element)
      for (const child of element.children) resize.observe(child)
      keepBottom()
    }
    observeContent()
    const content = new MutationObserver(observeContent)
    content.observe(element, { childList: true, subtree: true })
    return () => { resize.disconnect(); content.disconnect() }
  }, [jump])
  const onScroll = () => {
    const element = container.current
    if (!element) return
    nearBottom.current = element.scrollHeight - element.scrollTop - element.clientHeight < 80
    setAtBottom(nearBottom.current)
    if (nearBottom.current) setNewMessages(false)
  }
  return { container, atBottom, newMessages, jump, onScroll }
}
